import { atom } from 'jotai';

type NotificationKind =
  'error' | 'info' | 'info-square' | 'success' | 'warning' | 'warning-alt';

/**
 * Notification groups for the atomised notifications system.
 * Add new groups here to group notifications together.
 */
export const NotificationGroup = {
  DcaRegression: 'dca-workspace-regression',
  DcaActiveJob: 'dca-workspace-active-job',
  DcaWorkspaceUpdate: 'dca-workspace-update',
  DcaWorkspacePromoteWells: 'dca-workspace-promote-wells',
  DcaWorkspaceExport: 'dca-workspace-export',
  DcaWorkspaceIndexing: 'dca-workspace-indexing',
  StructuredParties: 'structured-parties',
  DifferentialScenarios: 'differential-scenarios',
  ExpenseScenarios: 'expense-scenarios',
  BulkAssetBatch: 'bulk-asset-batch',
} as const;

export type NotificationGroup =
  (typeof NotificationGroup)[keyof typeof NotificationGroup];

export interface AppNotification {
  id: string;
  title: string;
  message: string;
  kind: NotificationKind;
  hideCloseButton?: boolean;
  timestamp?: number;
  /** Auto-dismiss this notification after the given number of milliseconds. */
  autoHideMs?: number;
}

export type NotificationState = Partial<
  Record<NotificationGroup, AppNotification[]>
>;

export const notificationsAtom = atom<NotificationState>({});

export const addNotificationsAtom = atom(
  null,
  (get, set, groupId: NotificationGroup, notifications: AppNotification[]) => {
    const current = get(notificationsAtom);
    const existing = current[groupId] ?? [];
    const existingIds = new Set(existing.map((n) => n.id));

    const incoming = notifications
      .filter((n) => !existingIds.has(n.id))
      .map((n) => ({ ...n, timestamp: n.timestamp ?? Date.now() }));

    if (incoming.length === 0) return;

    set(notificationsAtom, {
      ...current,
      [groupId]: [...existing, ...incoming],
    });
  }
);

export const setNotificationsAtom = atom(
  null,
  (get, set, groupId: NotificationGroup, notifications: AppNotification[]) => {
    const current = get(notificationsAtom);
    set(notificationsAtom, {
      ...current,
      [groupId]: notifications.map((n) => ({
        ...n,
        timestamp: n.timestamp ?? Date.now(),
      })),
    });
  }
);

export const dismissNotificationAtom = atom(
  null,
  (get, set, notificationId: string) => {
    const current = get(notificationsAtom);
    const next: NotificationState = {};
    let changed = false;

    for (const [key, items] of Object.entries(current)) {
      const gid = key as NotificationGroup;
      const filtered = items!.filter((n) => n.id !== notificationId);
      if (filtered.length !== items!.length) changed = true;
      if (filtered.length > 0) next[gid] = filtered;
    }

    if (changed) set(notificationsAtom, next);
  }
);

export const clearNotificationGroupAtom = atom(
  null,
  (get, set, groupId: NotificationGroup) => {
    const current = get(notificationsAtom);
    if (!current[groupId]) return;

    const next = { ...current };
    delete next[groupId];
    set(notificationsAtom, next);
  }
);

export const clearAllNotificationsAtom = atom(null, (_, set) => {
  set(notificationsAtom, {});
});
